/**
 * Public enquiry (Book Shipment) API client.
 *
 * Posts a booking to the no-auth endpoint:
 *   POST {BASE}/api/v1/enquiries/public/:orgSlug
 * which records it as a new PENDING enquiry (ENQ#####). It then appears in the
 * admin web Enquiries module. The backend validates strictly
 * (forbidNonWhitelisted), so only fields declared on CreateEnquiryDto are sent.
 */

import { nationalDigits } from '../data/dialCodes';
import type { LocationResult } from '../components/ui/LocationSearch';

const BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:4000';
const ORG_SLUG = process.env.REACT_APP_ORG_SLUG ?? 'sahari-cargo';

type Party = {
  name: string;
  email: string;
  country: string;
  /** Dial code, e.g. `+971`. Prefixed onto `phone` on submit. */
  phoneCode: string;
  phone: string;
  /** Google Places pick → enquiry `origin` / `destination` + its geo columns. */
  location: LocationResult | null;
  /** Door-level detail → enquiry `pickupAddress` / `receiverAddress`. */
  address: string;
};

export type BookingPayload = {
  shipmentType: 'international' | 'domestic';
  shipper: Party;
  receiver: Party;
  shipment: {
    content: string;
    estimatedWeight: string; // kilograms
    numberOfPieces: string;
    pickupDate: string; // YYYY-MM-DD, or '' when not chosen
  };
  payment: string; // 'pickup' | 'online' | 'delivery'
};

const PAYMENT_MAP: Record<string, string> = {
  pickup: 'cash_on_pickup',
  online: 'online',
  delivery: 'cash_on_delivery',
};

/**
 * `+971` + `50 123 4567` → `+971501234567`.
 *
 * Shares `nationalDigits` with the form's validation so the number that gets
 * validated is exactly the number that gets submitted. Returns '' for an empty
 * number so the caller's truthiness guards still work.
 */
const composePhone = (p: Party): string => {
  const national = nationalDigits(p.phone);
  return national ? `${p.phoneCode}${national}` : '';
};

/**
 * Spreads a picked place into the enquiry's geo columns, prefixed `origin` for
 * the shipper and `destination` for the receiver. The coordinates are what let
 * `createPublic` resolve the nearest branch and what the driver app navigates
 * to, so they travel with the address rather than being re-geocoded later.
 */
const geoFields = (
  loc: LocationResult | null,
  prefix: 'origin' | 'destination',
): Record<string, unknown> => {
  if (!loc) return {};
  return {
    [prefix]: loc.formattedAddress,
    [`${prefix}Latitude`]: loc.latitude,
    [`${prefix}Longitude`]: loc.longitude,
    ...(loc.city ? { [`${prefix}City`]: loc.city } : {}),
    ...(loc.state ? { [`${prefix}State`]: loc.state } : {}),
    ...(loc.countryCode ? { [`${prefix}CountryCode`]: loc.countryCode } : {}),
    ...(loc.placeId ? { [`${prefix}PlaceId`]: loc.placeId } : {}),
  };
};

/**
 * Submit the Book Shipment flow as a public enquiry.
 * @returns the server-generated enquiry number (e.g. `ENQ00042`).
 */
export async function submitBooking(
  b: BookingPayload,
  captchaToken?: string | null,
): Promise<{ enquiryNumber: string }> {
  const weight = Number(b.shipment.estimatedWeight);
  const pieces = parseInt(b.shipment.numberOfPieces, 10);

  const body: Record<string, unknown> = {
    customerName: b.shipper.name.trim(),
    customerPhone: composePhone(b.shipper),
    ...(b.shipper.email.trim() ? { customerEmail: b.shipper.email.trim() } : {}),
    ...geoFields(b.shipper.location, 'origin'),
    ...(b.shipper.address.trim()
      ? { pickupAddress: b.shipper.address.trim() }
      : {}),
    ...geoFields(b.receiver.location, 'destination'),
    ...(b.receiver.name.trim() ? { receiverName: b.receiver.name.trim() } : {}),
    ...(composePhone(b.receiver) ? { receiverPhone: composePhone(b.receiver) } : {}),
    ...(b.receiver.address.trim()
      ? { receiverAddress: b.receiver.address.trim() }
      : {}),
    ...(Number.isFinite(weight) && weight >= 0
      ? { estimatedWeight: weight }
      : {}),
    ...(Number.isFinite(pieces) && pieces >= 1
      ? { numberOfPieces: pieces }
      : {}),
    ...(b.shipment.pickupDate ? { pickupDate: b.shipment.pickupDate } : {}),
    ...(PAYMENT_MAP[b.payment] ? { paymentMethod: PAYMENT_MAP[b.payment] } : {}),
    notes: [
      `Booking: ${b.shipmentType === 'international' ? 'International' : 'Domestic'}`,
      // The enquiry table has no free-text content column — `commodityTypeId` is
      // a UUID reference — so shipment content rides along in `notes`.
      b.shipment.content ? `Content: ${b.shipment.content}` : '',
      // Payment method now comes from the org's Master Payment Types, which
      // don't map to the fixed paymentMethod enum — capture it as a note.
      !PAYMENT_MAP[b.payment] && b.payment ? `Payment: ${b.payment}` : '',
    ]
      .filter(Boolean)
      .join(' · '),
  };

  let res: Response;
  try {
    res = await fetch(`${BASE}/api/v1/enquiries/public/${ORG_SLUG}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(captchaToken ? { 'cf-turnstile-response': captchaToken } : {}),
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error(
      'Could not reach the server. Please check your connection and try again.',
    );
  }

  if (!res.ok) {
    let message = 'Could not submit your booking. Please try again.';
    try {
      const j = await res.json();
      const m = j?.message ?? j?.error;
      if (typeof m === 'string') message = m;
      else if (Array.isArray(m) && m.length) message = m[0];
    } catch {
      /* keep default */
    }
    throw new Error(message);
  }

  const json = await res.json().catch(() => ({}));
  const enquiry = json?.data ?? json;
  return { enquiryNumber: enquiry?.enquiryNumber ?? '' };
}
